"use client";

import { unwrapCountedObjects } from "@/components/ProjectsInArtwork/utils/artworkListApiTransform";
import {
  normalizeVendorAssessmentListItem,
} from "@/components/VendorAssessmentList/utils/vendorAssessmentListApiTransform";
import type {
  BusinessReviewPanelListItem,
  BusinessReviewPanelListRequest,
  BusinessReviewPanelListResponse,
} from "../types/businessReviewPanelListApi.types";

export {
  formatVendorAssessmentStatusLabel as formatBusinessReviewPanelStatusLabel,
  resolveVendorPublished as resolveBusinessReviewPanelPublished,
  isVendorAssessmentStatusValue as isBusinessReviewPanelVendorAssessmentStatus,
} from "@/components/VendorAssessmentList/utils/vendorAssessmentListApiTransform";

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim().length > 0) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

function str(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v).trim();
  }
  return "";
}

export function normalizeBusinessReviewPanelListItem(
  raw: unknown,
  fallbackId: string
): BusinessReviewPanelListItem {
  const base = normalizeVendorAssessmentListItem(raw, fallbackId) as BusinessReviewPanelListItem;
  const r = (raw != null && typeof raw === "object" && !Array.isArray(raw) ? raw : {}) as Record<
    string,
    unknown
  >;
  return {
    ...base,
    onHoldDate: r.onHoldDate == null ? r.onHoldDate ?? null : str(r.onHoldDate) || null,
  };
}

export function normalizeBusinessReviewPanelListResponse(
  raw: unknown,
  req: BusinessReviewPanelListRequest,
  totalFromHeader?: unknown
): BusinessReviewPanelListResponse {
  if (Array.isArray(raw)) {
    const headerTotal = toNumber(totalFromHeader);
    return {
      items: raw.map((it, idx) => normalizeBusinessReviewPanelListItem(it, `${req.pageNo}-${idx}`)),
      totalCount: headerTotal ?? raw.length,
      pageNo: req.pageNo,
      pageSize: req.pageSize,
    };
  }

  const rawAsUnknown = (raw != null && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const itemsContainer =
    rawAsUnknown.items ??
    rawAsUnknown.data ??
    rawAsUnknown.content ??
    rawAsUnknown.objects ??
    [];
  const unwrappedItems = unwrapCountedObjects(itemsContainer);

  const headerTotal = toNumber(totalFromHeader);
  const bodyTotal =
    toNumber(rawAsUnknown.totalCount) ??
    toNumber(rawAsUnknown.totalElements) ??
    toNumber(rawAsUnknown.totalRecords) ??
    toNumber(rawAsUnknown.count);

  const normalizedItems = unwrappedItems.map((it, idx) =>
    normalizeBusinessReviewPanelListItem(it, `${req.pageNo}-${idx}`)
  );

  return {
    items: normalizedItems,
    totalCount: headerTotal ?? bodyTotal ?? normalizedItems.length,
    pageNo: toNumber(rawAsUnknown.pageNo) ?? toNumber(rawAsUnknown.pageNumber) ?? req.pageNo,
    pageSize: toNumber(rawAsUnknown.pageSize) ?? toNumber(rawAsUnknown.size) ?? req.pageSize,
  };
}
